// @flow

import * as React from 'react';
import {useContext, useState, useEffect, useRef} from 'react';
import {
  ControlledContentGroup,
  UncontrolledContentGroup,
  TabSelector,
} from 'docs/components';
import {usePrefs} from 'docs/data';
import {ContentGroupItem} from 'docs/util';
import Box from 'sail/Box';
import {useContentAnimation} from '../../../shell/content/ContentAnimationProvider';
import './CodeTabGroup.scss';

type CodeTabGroupContextData = {
  codeTabGroupCopyIconDiv: null | HTMLDivElement,
  codeTabGroupID: null | string,
};

const CodeTabGroupContext = React.createContext<CodeTabGroupContextData>({
  codeTabGroupCopyIconDiv: null,
  codeTabGroupID: null,
});

export const useCodeTabGroupContext = (): CodeTabGroupContextData => {
  return useContext(CodeTabGroupContext);
};

type CodeTabPrefs =
  | 'lang-ios'
  | 'lang-android'
  | 'lang'
  | 'lang-js'
  | 'html-or-react'
  | 'react-hooks'
  | 'none';

type CodeTabGroupProps = {|
  items: Array<ContentGroupItem>,
  codeTabGroupID: string,
  pref?: CodeTabPrefs,
|};

export const CodeTabGroup = ({
  items,
  codeTabGroupID,
  pref,
}: CodeTabGroupProps) => {
  const {getPref, setPref} = usePrefs();
  const ref = useRef();
  const [copyIconDiv, setCopyIconDiv] = useState(null);

  useEffect(() => {
    // This ref is used so the CodeBlock can render its copy icon into
    // the tab selector via a portal. We need to use a useEffect and state
    // here because we are passing the ref through a context. Without this,
    // on initial client renders, the ref would be null and the CodeBlock
    // would not be able to render the icon.

    if (!ref.current) {
      return;
    }

    setCopyIconDiv(ref.current);
  }, []);

  const {setAnimatePref} = useContentAnimation();

  let contentGroup;

  if (pref && pref !== 'none') {
    contentGroup = (
      <ControlledContentGroup
        className="CodeTabGroup"
        id={codeTabGroupID}
        selector={
          <Box className="CodeTabGroup-selector">
            <TabSelector size="small" tabClassName="CodeTabGroup-tab" />
            <div ref={ref} />
          </Box>
        }
        items={items}
        contentProps={{className: 'CodeTabGroup-content'}}
        selectedId={getPref(pref)}
        onItemSelected={(item) => {
          setPref(pref, item.id);
          setAnimatePref(pref);
        }}
        pref={pref}
      />
    );
  } else {
    // If no pref is specified, the tab group is "uncontrolled", meaning it manages
    // its state internally.
    contentGroup = (
      <UncontrolledContentGroup
        className="CodeTabGroup"
        id={codeTabGroupID}
        items={items}
        contentProps={{className: 'CodeTabGroup-content'}}
        selector={
          <Box className="CodeTabGroup-selector">
            <TabSelector size="small" tabClassName="CodeTabGroup-tab" />
            <div ref={ref} />
          </Box>
        }
      />
    );
  }

  return (
    <CodeTabGroupContext.Provider
      value={{
        codeTabGroupID,
        codeTabGroupCopyIconDiv: copyIconDiv,
      }}
    >
      {contentGroup}
    </CodeTabGroupContext.Provider>
  );
};
